// (B) 플레인 List — antd List 대체(순수 시각/구조). antd DOM 미러링:
// .om-list(.om-list) > [OmSpinner(.om-spin-nested-loading>.om-spin-container)] > ul.om-list-items > li.om-list-item / .om-list-empty-text / .om-list-footer.
//
// ⚠ .om-list* 는 live antd List 소스 다수(OmList 8소비처 + ninegqlUI[batch9]) + ThreadList JS 쿼리
// (.om-list·.om-list-item·ul.om-list-items) + antd CSS-in-JS 공존. 따라서 이중 클래스 병존:
// .om-list*(소비처 SCSS·JS 쿼리 공유) + .om-list*(kit hook) 를 함께 부여 → SCSS·JS 무변경, rename 은 ninegqlUI 이관 시 일괄.
// loading 은 항상 OmSpinner 로 래핑(antd List 가 항상 Spin 래퍼 방출 → .om-spin-container 재현).
import { cloneElement, isValidElement, type Key } from 'react'
import type { OmwListItemProps, OmwListProps } from '../api/OmwList'
import { OmwEmpty } from '../api/OmwEmpty'
import { OmwSpinner } from '../api/OmwSpinner'
import { cx } from './cx'
import '../styles/omwList.css'

// antd List 파리티: dataSource 매핑 항목에 key 를 부여한다(antd 는 rowKey/item.key/index 로
// 자동 키잉). renderItem 결과의 자체 key → item.key → index 순으로 결정해 React key 경고를 없앤다.
function keyedItem(
  node: ReturnType<NonNullable<OmwListProps<unknown>['renderItem']>>,
  item: unknown,
  index: number,
) {
  if (!isValidElement(node)) return node
  const itemKey: Key =
    node.key ??
    (item != null && typeof item === 'object' && 'key' in item
      ? ((item as { key?: Key }).key ?? `om-list-item-${index}`)
      : `om-list-item-${index}`)
  return cloneElement(node, { key: itemKey })
}

export function PlainList<T>({
  dataSource,
  renderItem,
  itemLayout,
  emptyText,
  loading,
  footer,
  header,
  className,
  style,
  children,
  ...rest
}: OmwListProps<T>) {
  const hasItems = (dataSource?.length ?? 0) > 0

  const itemsNode = hasItems ? (
    <ul className="om-list-items">
      {dataSource?.map((item, index) => keyedItem(renderItem?.(item, index), item, index))}
      {children}
    </ul>
  ) : (
    <div className="om-list-empty-text">{emptyText ?? <OmwEmpty />}</div>
  )

  return (
    <div
      className={cx(
        'om-list',
        itemLayout === 'vertical' ? 'om-list-vertical' : 'om-list-horizontal',
        'om-list-split',
        className,
      )}
      style={style}
      {...rest}
    >
      {header != null && <div className="om-list-header">{header}</div>}
      <OmwSpinner
        spinning={loading?.spinning ?? false}
        wrapperClassName={loading?.wrapperClassName}
      >
        {itemsNode}
      </OmwSpinner>
      {footer != null && <div className="om-list-footer">{footer}</div>}
    </div>
  )
}

export function PlainListItem({
  className,
  style,
  onClick,
  onMouseDown,
  children,
  ...rest
}: OmwListItemProps) {
  return (
    <li
      className={cx('om-list-item', className)}
      style={style}
      onClick={onClick}
      onMouseDown={onMouseDown}
      {...rest}
    >
      {children}
    </li>
  )
}
